// SELECT-shape extractor — driven by clause-boundary scanning + per-clause
// parsers. Produces a `QueryStructure` with every SELECT field populated.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — SELECT.

import type { ParseOptions, StatementInfo } from '../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import { computeCteBindings } from '../scope/cteScope';
import type {
  ParsedBuffer,
  QueryStructure,
  SelectItem,
  FromItem,
  JoinClause,
  BooleanFragment,
  BooleanNode,
  NamedWindowDef,
  OrderByItem,
  LimitSpec,
  CompoundOperator,
} from '../types';
import type { IdentifierRef, SqlEngine } from '../../sql-grammar/model';

import { splitSelectClauses, clauseRange, selectListRange, type SelectClauseRanges } from './selectShape';
import { parseSelectItems } from './extractQueryStructure.selectList';
import { parseFromAndJoins } from './extractQueryStructure.from';
import { parseGroupByItems, parseOrderByItems, parseLimitSpec } from './extractQueryStructure.predicates';
import { parseBooleanTree, flattenBooleanTree } from './extractQueryStructure.booleanTree';
import { extractWindowClause } from './extractQueryStructure.windowClause';

// All five engines now support the `WINDOW w AS (…)` clause: postgres / mysql /
// sqlite have long had it, and SQL Server 2022 / Oracle 21c gained it. `window`
// is still a legal identifier on every engine, so `windows` is only populated
// when a real `<ident> [AS] (…)` definition actually parses — `extractWindowClause`
// / `parseDefinition` require a `(` to follow the identifier, so a bare `window`
// column/alias reference never populates this. The selectShape clause-splitter
// records `windowKw` ungated on all engines; this Set now admits all five.
const WINDOW_CLAUSE_ENGINES: ReadonlySet<SqlEngine> = new Set<SqlEngine>([
  'postgres', 'mysql', 'sqlite', 'mssql', 'oracle',
]);

export interface SelectExtractCtx {
  readonly buffer: ParsedBuffer;
  readonly source: string;
  readonly options: ParseOptions;
  readonly lineIdx: LineIndex;
}

export function extractSelectStructure(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): QueryStructure {
  const { source, options } = buffer.parsedDocument;
  const lineIdx = buildLineIndex(source);
  const ctx: SelectExtractCtx = { buffer, source, options, lineIdx };

  const ctes = computeCteBindings(buffer, statementInfo.id);
  const { branches, operators } = extractBranches(
    ctx, statementInfo.range.startOffset, statementInfo.range.endOffset,
  );
  if (branches.length === 0) {
    return {
      kind: 'select', statementRange: statementInfo.range,
      ...(ctes.length ? { ctes } : {}),
    };
  }
  const first = branches[0];
  return {
    ...first,
    statementRange: statementInfo.range,
    ...(ctes.length ? { ctes } : {}),
    ...(branches.length > 1 ? { compoundBranches: branches.slice(1) } : {}),
    // Parallel to compoundBranches (one operator per additional branch):
    // operators[i] precedes compoundBranches[i]. Length always equals
    // compoundBranches.length.
    ...(operators.length ? { compoundOperators: operators } : {}),
  };
}

// Walk the byte range and emit one QueryStructure per branch (split on
// top-level UNION / INTERSECT / EXCEPT; Oracle also MINUS), plus one
// CompoundOperator per separator (kind + ALL flag + modifier-inclusive range),
// left-to-right in declaration order so `operators[i]` precedes `branches[i+1]`.
function extractBranches(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): { branches: QueryStructure[]; operators: CompoundOperator[] } {
  const branches: QueryStructure[] = [];
  const operators: CompoundOperator[] = [];
  let i = start;
  while (i < end) {
    const clauses = splitSelectClauses(ctx.source, i, end, ctx.options);
    if (!clauses) break;
    const branchEnd = clauses.setOpKw ? clauses.setOpKw.start : end;
    branches.push(buildBranch(ctx, clauses, branchEnd));
    if (!clauses.setOpKw) break;
    // Consume the optional ALL/DISTINCT modifier after the set-op keyword and
    // record the separator. `range` spans the keyword THROUGH the modifier —
    // the exact span the modify-set-operator applicator replaces.
    let next = clauses.setOpKw.end;
    let rangeEnd = clauses.setOpKw.end;
    let all = false;
    while (next < end && /\s/.test(ctx.source[next])) next++;
    const after = ctx.source.slice(next, end).match(/^(ALL|DISTINCT)\b/i);
    if (after) {
      all = after[1].toUpperCase() === 'ALL';
      next += after[0].length;
      rangeEnd = next;
    }
    operators.push({
      kind: clauses.setOpUpper ?? 'UNION',
      all,
      range: rangeFromOffsets(ctx.lineIdx, clauses.setOpKw.start, rangeEnd),
    });
    i = next;
  }
  return { branches, operators };
}

// eslint-disable-next-line max-lines-per-function
function buildBranch(
  ctx: SelectExtractCtx,
  clauses: SelectClauseRanges,
  branchEnd: number,
): QueryStructure {
  const { source, options, lineIdx } = ctx;
  const slRange = selectListRange(source, options, clauses, branchEnd);
  const selectItems: readonly SelectItem[] = parseSelectItems(ctx, slRange.contentStart, slRange.contentEnd);

  let fromItems: readonly FromItem[] | undefined;
  let joins: readonly JoinClause[] | undefined;
  if (clauses.fromKw) {
    const r = clauseRange(clauses, clauses.fromKw, branchEnd);
    const split = parseFromAndJoins(ctx, r.contentStart, r.contentEnd);
    if (split.fromItems.length > 0) fromItems = split.fromItems;
    if (split.joins.length > 0) joins = split.joins;
  }

  let where: readonly BooleanFragment[] | undefined;
  let whereTree: BooleanNode | undefined;
  if (clauses.whereKw) {
    const r = clauseRange(clauses, clauses.whereKw, branchEnd);
    whereTree = parseBooleanTree(ctx, r.contentStart, r.contentEnd);
    // Derive the flat `where` from the tree so the two never diverge (parity-
    // tested). HAVING mirrors this below; JOIN-ON / DML keep using parseBooleanFragments.
    where = whereTree ? flattenBooleanTree(ctx, whereTree) : undefined;
  }

  let groupBy: readonly IdentifierRef[] | undefined;
  if (clauses.groupKw) {
    const r = clauseRange(clauses, clauses.groupKw, branchEnd);
    // GROUP BY — skip the `BY` token before parsing items.
    const afterBy = skipKeywordBy(source, r.contentStart, r.contentEnd);
    groupBy = parseGroupByItems(ctx, afterBy, r.contentEnd);
  }

  let having: readonly BooleanFragment[] | undefined;
  let havingTree: BooleanNode | undefined;
  if (clauses.havingKw) {
    const r = clauseRange(clauses, clauses.havingKw, branchEnd);
    havingTree = parseBooleanTree(ctx, r.contentStart, r.contentEnd);
    // Derive the flat `having` from the tree so the two never diverge — same
    // parseBooleanTree → flattenBooleanTree composition WHERE uses (parity-tested).
    having = havingTree ? flattenBooleanTree(ctx, havingTree) : undefined;
  }

  let windows: readonly NamedWindowDef[] | undefined;
  if (clauses.windowKw && WINDOW_CLAUSE_ENGINES.has(options.engine)) {
    const r = clauseRange(clauses, clauses.windowKw, branchEnd);
    const defs = extractWindowClause(ctx, r.contentStart, r.contentEnd);
    if (defs.length > 0) windows = defs;
  }

  let orderBy: readonly OrderByItem[] | undefined;
  if (clauses.orderKw) {
    const r = clauseRange(clauses, clauses.orderKw, branchEnd);
    const afterBy = skipKeywordBy(source, r.contentStart, r.contentEnd);
    orderBy = parseOrderByItems(ctx, afterBy, r.contentEnd);
  }

  let limit: LimitSpec | undefined;
  if (clauses.limitKw) {
    const r = clauseRange(clauses, clauses.limitKw, branchEnd);
    limit = parseLimitSpec(
      ctx,
      clauses.limitKw,
      clauses.limitKw.start,
      r.contentEnd,
    );
  } else if (clauses.top) {
    // MSSQL `SELECT TOP n` — synthesize a limit spec from the TOP argument.
    limit = synthesizeTopLimit(ctx, clauses, lineIdx);
  }

  const distinct = clauses.distinct ? true : (clauses.all ? false : undefined);

  return {
    kind: 'select',
    statementRange: rangeFromOffsets(lineIdx, clauses.selectKw.start, branchEnd),
    ...(distinct !== undefined ? { distinct } : {}),
    ...(selectItems.length ? { selectItems } : {}),
    ...(fromItems ? { fromItems } : {}),
    ...(joins ? { joins } : {}),
    ...(where ? { where } : {}),
    ...(whereTree ? { whereTree } : {}),
    ...(groupBy && groupBy.length ? { groupBy } : {}),
    ...(having ? { having } : {}),
    ...(havingTree ? { havingTree } : {}),
    ...(windows ? { windows } : {}),
    ...(orderBy && orderBy.length ? { orderBy } : {}),
    ...(limit ? { limit } : {}),
  };
}

function skipKeywordBy(source: string, start: number, end: number): number {
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  if (source.slice(i, i + 2).toUpperCase() === 'BY' && /[^A-Za-z_0-9]/.test(source[i + 2] ?? ' ')) {
    return i + 2;
  }
  return start;
}

function synthesizeTopLimit(
  ctx: SelectExtractCtx,
  clauses: SelectClauseRanges,
  lineIdx: LineIndex,
): LimitSpec | undefined {
  if (!clauses.top) return undefined;
  const { source, options } = ctx;
  // Scan from after `TOP` to the next clause boundary.
  let i = clauses.top.end;
  while (i < source.length && /\s/.test(source[i])) i++;
  const argStart = i;
  if (source[i] === '(') {
    let d = 1; i++;
    while (i < source.length && d > 0) {
      if (source[i] === '(') d++;
      else if (source[i] === ')') d--;
      i++;
    }
  } else {
    while (i < source.length && /[0-9.]/.test(source[i])) i++;
  }
  const count = source.slice(argStart, i).trim();
  void options;
  return {
    count: count || undefined,
    range: rangeFromOffsets(lineIdx, clauses.top.start, i),
  };
}
